import React, { useState, useEffect } from "react";
import { cityNamesMap } from "../utils/cityForShabbat.js";
import { customFetch } from "../utils/custom-fetch.js";
import PropTypes from "prop-types";
import styled from "styled-components";

const GOLD = "#F7CE84";

// ─── Layout config ────────────────────────────────────────────────────────────
// x  = distance from the LEFT edge of the card  (%)
// y  = distance from the TOP  edge of the card  (%)
// w  = element width                             (%)
// h  = element height (only desc)               (%)
// Adjust these numbers to reposition each element without touching anything else.
const LAYOUT = {
  city:        { x: "20%", y: "16%",  w: "42%" },
  candleTime:  { x: "67%", y: "16%",  w: "24%" },
  havdalahTime:{ x: "67%", y: "31%",  w: "24%" },
  parasha:     { x: "25%", y: "42%",  w: "66%" },
  desc:        { x: "18%", y: "53%",  w: "76%", h: "52%" },
};
// ─────────────────────────────────────────────────────────────────────────────

const Card = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-image: ${({ $image }) => ($image ? `url(${$image})` : "none")};
  background-color: #0a1628;
  background-size: 100% 100%;
  overflow: hidden;
  direction: rtl;
  font-family: ${({ $fontFamily }) => $fontFamily || "'Varela Round', sans-serif"};
`;

/* Generic absolutely-positioned container */
const Elem = styled.div`
  position: absolute;
  left: ${(p) => p.$x};
  top: ${(p) => p.$y};
  width: ${(p) => p.$w};
  ${(p) => p.$h && `height: ${p.$h}; overflow: hidden;`}
`;

const CityText = styled.h2`
  color: ${GOLD};
  font-size: 2em;
  font-weight: 900;
  text-align: center;
  margin: 0;
  letter-spacing: 0.05em;
  line-height: 1.2;
  direction: rtl;
`;

const TimeText = styled.span`
  color: ${GOLD};
  font-size: 1.4em;
  font-weight: 900;
  display: block;
  text-align: center;
  direction: ltr;
`;

const ParashaText = styled.span`
  color: #fff;
  font-size: 1.1em;
  font-weight: 700;
  display: block;
  text-align: center;
  direction: rtl;
`;

const DescText = styled.p`
  color: rgba(255, 255, 255, 0.87);
  font-size: 0.88em;
  line-height: 1.6;
  text-align: right;
  margin: 0;
  direction: rtl;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StatusMsg = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.8em;
  text-align: center;
`;

const extractTime = (s) => (s?.includes(": ") ? s.split(": ")[1] : (s ?? ""));

const Shabbat = ({ hebrewCityName, image, fontFamily }) => {
  const [shabbatDetails, setShabbatDetails] = useState({
    candleLighting: null,
    havdalah: null,
    parashat: null,
  });
  const englishCityName = cityNamesMap[hebrewCityName];
  const [parashaDescription, setParashaDescription] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const toLocalDateStr = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    const RETRY_MS = 2 * 60 * 1000;
    const REFRESH_MS = 30 * 60 * 1000;
    let timeoutId = null;

    const fetchShabbatTimes = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!englishCityName) throw new Error("עיר לא נמצאה במיפוי");

        const today = new Date();
        const dayOfWeek = today.getDay();
        const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
        const nextFriday = new Date(today);
        nextFriday.setDate(today.getDate() + daysUntilFriday);

        const nextSaturday = new Date(nextFriday);
        nextSaturday.setDate(nextFriday.getDate() + 1);

        const start = toLocalDateStr(nextFriday);
        const end = toLocalDateStr(nextSaturday);
        const url = `https://www.hebcal.com/hebcal?v=1&cfg=json&maj=on&ss=on&mf=on&c=on&geo=city&city=${englishCityName}&start=${start}&end=${end}&M=on&s=on`;

        console.log(`[Shabbat] Fetching for ${hebrewCityName} (${englishCityName}): ${url}`);

        const response = await fetch(url);
        if (!response.ok) throw new Error(`שגיאת רשת: ${response.status}`);

        const data = await response.json();

        let candleLighting = null;
        let havdalah = null;
        let parashat = null;

        const candleItems = data.items.filter((item) => item.category === "candles");
        candleItems.sort((a, b) => new Date(a.date) - new Date(b.date));

        const nextFridayStart = new Date(nextFriday);
        nextFridayStart.setHours(0, 0, 0, 0);
        const nextFridayEnd = new Date(nextFriday);
        nextFridayEnd.setHours(23, 59, 59, 999);

        const matchedCandle = candleItems.find((cand) => {
          const candDate = new Date(cand.date);
          return candDate >= nextFridayStart && candDate <= nextFridayEnd;
        });

        if (matchedCandle) {
          candleLighting = matchedCandle.title;
        } else if (candleItems.length > 0) {
          candleLighting = candleItems[0].title;
        }

        data.items.forEach((item) => {
          if (item.category === "havdalah") {
            havdalah = item.title;
          } else if (item.category === "parashat") {
            parashat = item.hebrew;
          } else if (item.category === "holiday") {
            parashat = item.hebrew;
          }
        });

        const isComplete = !!(candleLighting && havdalah && parashat);
        const delay = isComplete ? REFRESH_MS : RETRY_MS;
        console.log(
          `[Shabbat] Data ${isComplete ? "complete" : "incomplete"} — next fetch in ${delay / 60000} min`,
          { candleLighting, havdalah, parashat }
        );

        setShabbatDetails({ candleLighting, havdalah, parashat });
        timeoutId = setTimeout(fetchShabbatTimes, delay);
      } catch (err) {
        console.error(`[Shabbat] Error:`, err);
        setError(err.message);
        timeoutId = setTimeout(fetchShabbatTimes, RETRY_MS);
      } finally {
        setIsLoading(false);
      }
    };

    if (englishCityName) {
      fetchShabbatTimes();
    } else {
      setError(`העיר "${hebrewCityName}" לא נמצאה`);
      setIsLoading(false);
    }

    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [englishCityName, hebrewCityName]);

  useEffect(() => {
    const fetchParashaDescription = async () => {
      if (!shabbatDetails.parashat) return;
      try {
        const parashaName = encodeURIComponent(shabbatDetails.parashat);
        const res = await customFetch.get(`/parashot/${parashaName}`);
        setParashaDescription(res.data?.description ?? null);
      } catch (err) {
        console.error(`[Parasha] Error:`, err);
        setParashaDescription(null);
      }
    };
    fetchParashaDescription();
  }, [shabbatDetails.parashat]);

  return (
    <Card $image={image} $fontFamily={fontFamily}>

      {/* ── City name ─────────────────────────────── */}
      <Elem $x={LAYOUT.city.x} $y={LAYOUT.city.y} $w={LAYOUT.city.w}>
        <CityText>{hebrewCityName}</CityText>
      </Elem>

      {/* ── Candle lighting time ──────────────────── */}
      {shabbatDetails.candleLighting ? (
        <Elem $x={LAYOUT.candleTime.x} $y={LAYOUT.candleTime.y} $w={LAYOUT.candleTime.w}>
          <TimeText>{extractTime(shabbatDetails.candleLighting)}</TimeText>
        </Elem>
      ) : isLoading ? (
        <StatusMsg>טוען...</StatusMsg>
      ) : null}

      {/* ── Havdalah time ─────────────────────────── */}
      {shabbatDetails.havdalah && (
        <Elem $x={LAYOUT.havdalahTime.x} $y={LAYOUT.havdalahTime.y} $w={LAYOUT.havdalahTime.w}>
          <TimeText>{extractTime(shabbatDetails.havdalah)}</TimeText>
        </Elem>
      )}

      {/* ── Parasha title ─────────────────────────── */}
      {shabbatDetails.parashat && (
        <Elem $x={LAYOUT.parasha.x} $y={LAYOUT.parasha.y} $w={LAYOUT.parasha.w}>
          <ParashaText>{shabbatDetails.parashat}</ParashaText>
        </Elem>
      )}

      {/* ── Parasha description ───────────────────── */}
      {parashaDescription && (
        <Elem $x={LAYOUT.desc.x} $y={LAYOUT.desc.y} $w={LAYOUT.desc.w} $h={LAYOUT.desc.h}>
          <DescText>{parashaDescription}</DescText>
        </Elem>
      )}

      {/* ── Error ─────────────────────────────────── */}
      {error && (
        <StatusMsg style={{ color: "#ff6b6b" }}>{error}</StatusMsg>
      )}

    </Card>
  );
};

Shabbat.propTypes = {
  hebrewCityName: PropTypes.string.isRequired,
  image: PropTypes.string,
  fontFamily: PropTypes.string,
};

Shabbat.defaultProps = {
  image: "",
  fontFamily: "'Varela Round', sans-serif",
};

export default Shabbat;
